import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import {
  StatusCode,
  patchCustomerPeriodicities,
  type PeriodicityTypes,
  type PaginatedPeriodicityListList as CustomerPeriodicitiesResponse,
} from '@hungryroot/rest';

interface Params {
  customerId: number;
  data: {
    product: number;
    periodicity: PeriodicityTypes;
  };
}

export const getPeriodicitiesQueryKey = () => ['Customers', 'periodicities'];

export function usePatchCustomerPeriodicities() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: Params) => {
      const { customerId, data } = params;
      const resp = await patchCustomerPeriodicities({
        customerId,
        data: [data],
      });
      if (resp.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(resp.data, null, 2));
      }
      return resp.data;
    },
    onMutate: async (params) => {
      const { customerId, data } = params;

      await queryClient.cancelQueries({
        queryKey: getPeriodicitiesQueryKey(),
      });

      const existingCustomerPeriodicities =
        queryClient.getQueriesData<CustomerPeriodicitiesResponse>({
          queryKey: getPeriodicitiesQueryKey(),
        });

      existingCustomerPeriodicities.forEach(([queryKey, results]) => {
        const next = produce(results, (draft) => {
          if (draft === undefined) {
            return;
          }
          draft.results = draft.results?.length
            ? [
                ...draft.results,
                {
                  customer_id: customerId,
                  product: data.product,
                  periodicity: data.periodicity,
                },
              ]
            : [
                {
                  customer_id: customerId,
                  product: data.product,
                  periodicity: data.periodicity,
                },
              ];
        });
        queryClient.setQueryData(queryKey, next);
      });
      return { existingCustomerPeriodicities };
    },
    onError: (
      err,
      params,
      context:
        | {
            existingCustomerPeriodicities: unknown;
          }
        | undefined
    ) => {
      queryClient.setQueryData(
        getPeriodicitiesQueryKey(),
        context?.existingCustomerPeriodicities
      );
    },
    onSettled: (data, err, params) => {
      const {
        data: { periodicity, product },
      } = params;

      queryClient.invalidateQueries({
        queryKey: getPeriodicitiesQueryKey(),
      });

      // dispatch an event here so all UI gets the latest values
      // This is a DOM event because this data is displayed
      // in React and Coffee Script.
      // Once we are 100% on React, we can remove this in favor of refreshing
      // data from the server.
      const evt = new CustomEvent('product-periodicity-changed', {
        detail: {
          productId: product,
          periodicity: periodicity,
        },
      });
      document.dispatchEvent(evt);
    },
  });
}
